// [SERVICE] Plans API Service - Gestion Plans AI Credits
// [RÔLE] Orchestration business-level pour activation, changement, annulation plans
// [ARCHITECTURE] Wrapper /api/v1/plans/* avec validation transitions

import FamlaServices from "./FamlaServices";

/**
 * Service pour gérer les plans AI Credits
 * Communique avec les endpoints /api/v1/plans/*
 */
class PlansService {
    /**
     * Active un plan payant (ESSENTIAL/PLUS/ENTERPRISE)
     * @param plan - Plan à activer ('ESSENTIAL' | 'PLUS' | 'ENTERPRISE')
     * @param companyId - ID de la company
     * @param accessToken - Token JWT d'authentification
     * @returns Session checkout ou action manuelle selon le plan
     */
    static async activatePlan(
        plan: string,
        companyId: string,
        accessToken: string
    ) {
        try {
            // Générer URLs de retour
            const baseUrl = window.location.origin;
            const successUrl = `${baseUrl}/payment-success?plan=${plan}`;
            const cancelUrl = `${baseUrl}/payment-cancel`;

            const body = {
                company_id: companyId, // ✅ Ajouté: nécessaire pour identifier la company
                plan: plan,
                payment_method_id: "not_used_in_checkout_mode", // Ignoré en mode Checkout
                billing_period: "monthly",
                metadata: {
                    success_url: successUrl,
                    cancel_url: cancelUrl
                }
            };

            const response = await FamlaServices(
                "api_host",
                `/plans/activate`,
                "POST",
                body,
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data, // Contient action, session_url, etc.
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 400) {
                return {
                    success: false,
                    error: "Bad Request",
                    message: response.body.description || "Invalid plan or request",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to activate plan",
                };
            }
        } catch (error) {
            console.error("[PlansService] activatePlan error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Change le plan actuel vers un nouveau plan
     * @param companyId - ID de la company
     * @param newPlan - Nouveau plan ('FREE' | 'ESSENTIAL' | 'PLUS' | 'ENTERPRISE')
     * @param changeReason - Raison: 'upgrade' | 'downgrade' | 'other'
     * @param note - Note optionnelle
     * @param accessToken - Token JWT
     * @returns Action à effectuer (redirect_checkout, subscription_cancel_required, manual)
     */
    static async changePlan(
        companyId: string,
        newPlan: string,
        changeReason: string,
        note: string | null,
        accessToken: string
    ) {
        try {
            const body = {
                company_id: companyId, // ✅ Ajouté: nécessaire pour identifier la company
                new_plan: newPlan,
                change_reason: changeReason,
                payment_method_id: null, // Pas utilisé en mode Checkout
                note: note
            };

            const response = await FamlaServices(
                "api_host",
                `/plans/change`,
                "PUT",
                body,
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data, // Contient action, session_url, steps, etc.
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 400) {
                return {
                    success: false,
                    error: "Bad Request",
                    message: response.body.description || "Invalid plan change request",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to change plan",
                };
            }
        } catch (error) {
            console.error("[PlansService] changePlan error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Annule le plan actif (principalement PLUS)
     * @param companyId - ID de la company
     * @param cancelReason - Raison: 'cancellation' | 'payment_failed' | 'other'
     * @param immediate - Annulation immédiate (true) ou fin période (false)
     * @param note - Note optionnelle
     * @param accessToken - Token JWT
     * @returns Confirmation annulation
     */
    static async cancelPlan(
        companyId: string,
        cancelReason: string,
        immediate: boolean,
        note: string | null,
        accessToken: string
    ) {
        try {
            const body = {
                company_id: companyId, // ✅ Ajouté: nécessaire pour identifier la company
                cancel_reason: cancelReason,
                note: note,
                immediate: immediate
            };

            const response = await FamlaServices(
                "api_host",
                `/plans/cancel`,
                "DELETE",
                body,
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 400) {
                return {
                    success: false,
                    error: "Bad Request",
                    message: response.body.description || "Cannot cancel this plan",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to cancel plan",
                };
            }
        } catch (error) {
            console.error("[PlansService] cancelPlan error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Achète un top-up de crédits (ESSENTIAL uniquement)
     * @param companyId - ID de la company
     * @param credits - Nombre de crédits à acheter (40, 80, 120...)
     * @param accessToken - Token JWT
     * @returns Session checkout pour redirection Stripe
     */
    static async purchaseTopUp(
        companyId: string,
        credits: number,
        accessToken: string
    ) {
        try {
            // Générer URLs de retour
            const baseUrl = window.location.origin;
            const successUrl = `${baseUrl}/payment-success?topup=true&plan=ESSENTIAL`;
            const cancelUrl = `${baseUrl}/payment-cancel`;

            const body = {
                company_id: companyId, // ✅ Ajouté: nécessaire pour identifier la company
                credits: credits,
                payment_method_id: "not_used_in_checkout_mode", // Ignoré en mode Checkout
                metadata: {
                    success_url: successUrl,
                    cancel_url: cancelUrl
                }
            };

            const response = await FamlaServices(
                "api_host",
                `/topup/purchase`,
                "POST",
                body,
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data, // Contient session_url, session_id, expires_at
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 400) {
                return {
                    success: false,
                    error: "Bad Request",
                    message: response.body.description || "Top-up only available for ESSENTIAL plan",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to purchase top-up",
                };
            }
        } catch (error) {
            console.error("[PlansService] purchaseTopUp error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Récupère les plans disponibles pour changement
     * @param companyId - ID de la company
     * @param accessToken - Token JWT
     * @returns Plans disponibles + recommendations selon plan actuel
     */
    static async getAvailablePlans(companyId: string, accessToken: string) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/plans/available/${companyId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data, // Contient current_plan, available_plans, recommendations
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 404) {
                return {
                    success: false,
                    error: "Not Found",
                    message: response.body.description || "Credit account not found",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch available plans",
                };
            }
        } catch (error) {
            console.error("[PlansService] getAvailablePlans error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Récupère l'historique des changements de plans
     * @param companyId - ID de la company
     * @param limit - Nombre max entrées (défaut 50)
     * @param skip - Offset pagination (défaut 0)
     * @param accessToken - Token JWT
     * @returns Historique complet avec détails
     */
    static async getPlanHistory(
        companyId: string,
        limit: number = 50,
        skip: number = 0,
        accessToken: string
    ) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/plans/history/${companyId}?limit=${limit}&skip=${skip}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data, // Contient total, history[]
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else if (response.status === 404) {
                return {
                    success: false,
                    error: "Not Found",
                    message: response.body.description || "No plan history found",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch plan history",
                };
            }
        } catch (error) {
            console.error("[PlansService] getPlanHistory error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Formate le message d'erreur pour affichage utilisateur
     * @param error - Erreur retournée par l'API
     * @returns Message d'erreur formaté
     */
    static formatErrorMessage(error: any): string {
        if (typeof error === 'string') return error;
        if (error?.message) return error.message;
        if (error?.error === 'Network Error') {
            return 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
        }
        if (error?.error === 'Unauthorized') {
            return 'Session expirée. Veuillez vous reconnecter.';
        }
        return 'Une erreur est survenue. Veuillez réessayer ou contacter le support.';
    }
}

export default PlansService;
